// Maps each chapter to an emotional "mood" so the background atmosphere fits.
// Strategy: a sensible default mood per book, with hand-picked overrides for
// famous chapters. Moods: wonder, peace, prayer, majesty, battle, lament,
// passion, celebration, prophecy, wilderness.

const BOOK_MOOD = {
  GEN: "wonder", EXO: "wilderness", LEV: "prayer", NUM: "wilderness", DEU: "prayer",
  JOS: "battle", JDG: "battle", RUT: "peace", "1SA": "battle", "2SA": "battle",
  "1KI": "majesty", "2KI": "majesty", "1CH": "majesty", "2CH": "majesty",
  EZR: "prayer", NEH: "prayer", EST: "celebration", JOB: "lament", PSA: "prayer",
  PRO: "peace", ECC: "lament", SNG: "peace", ISA: "majesty", JER: "lament",
  LAM: "lament", EZK: "prophecy", DAN: "prophecy", HOS: "lament", JOL: "prophecy",
  AMO: "lament", OBA: "prophecy", JON: "wilderness", MIC: "prophecy", NAM: "battle",
  HAB: "prayer", ZEP: "prophecy", HAG: "prophecy", ZEC: "prophecy", MAL: "prophecy",
  MAT: "peace", MRK: "peace", LUK: "peace", JHN: "peace", ACT: "celebration",
  ROM: "prayer", "1CO": "prayer", "2CO": "prayer", GAL: "prayer", EPH: "prayer",
  PHP: "celebration", COL: "prayer", "1TH": "prayer", "2TH": "prayer", "1TI": "prayer",
  "2TI": "prayer", TIT: "prayer", PHM: "prayer", HEB: "majesty", JAS: "prayer",
  "1PE": "prayer", "2PE": "prayer", "1JN": "peace", "2JN": "peace", "3JN": "peace",
  JUD: "prophecy", REV: "majesty",
};

// chapter-specific overrides, keyed "BID_C"
const OVERRIDES = {
  GEN_1: "wonder", GEN_2: "wonder", GEN_22: "passion",
  EXO_14: "battle", EXO_15: "celebration", EXO_20: "majesty",
  JOS_6: "battle",
  PSA_8: "wonder", PSA_19: "wonder", PSA_23: "peace", PSA_42: "lament",
  PSA_51: "lament", PSA_91: "peace", PSA_103: "celebration", PSA_139: "wonder",
  PSA_148: "celebration", PSA_150: "celebration",
  ISA_6: "majesty", ISA_40: "majesty", ISA_53: "passion",
  DAN_3: "prophecy", DAN_6: "prophecy",
  MAT_5: "prayer", MAT_6: "prayer", MAT_26: "passion", MAT_27: "passion", MAT_28: "celebration",
  MRK_14: "passion", MRK_15: "passion", MRK_16: "celebration",
  LUK_2: "wonder", LUK_22: "passion", LUK_23: "passion", LUK_24: "celebration",
  JHN_1: "wonder", JHN_15: "peace", JHN_17: "prayer",
  JHN_18: "passion", JHN_19: "passion", JHN_20: "celebration",
  ACT_2: "celebration",
  ROM_8: "celebration",
  "1CO_13": "peace",
  REV_4: "majesty", REV_5: "majesty", REV_21: "celebration", REV_22: "celebration",
};

export function moodForChapter(book, chapter) {
  return OVERRIDES[`${book}_${chapter}`] || BOOK_MOOD[book] || "peace";
}

// which nature sound best fits a mood (nature mode)
const MOOD_TO_NATURE = {
  wonder: "ocean", peace: "river", prayer: "fire", majesty: "ocean",
  battle: "wind", lament: "rain", passion: "rain", celebration: "birds",
  prophecy: "wind", wilderness: "wind",
};

// deterministic pick so the same chapter always gets the same track
function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// Sequential rotation per music mode so a reader cycles through the WHOLE
// library before any track repeats. The pointer is PERSISTED across sessions, so
// a once-a-day reader hears a different track each day instead of always starting
// on the first one. `_seen` keeps a given chapter stable within a session.
const ROT_KEY = "fw_atmo_rot";
function loadRot() {
  try { return JSON.parse(localStorage.getItem(ROT_KEY)) || {}; } catch { return {}; }
}
const _rot = loadRot(); // mode -> next index (persisted)
const _seen = {};        // `${mode}|${key}` -> chosen id (session only)
function rotatePick(pool, mode, key) {
  const memoKey = `${mode}|${key}`;
  if (_seen[memoKey] && pool.some((t) => t.id === _seen[memoKey])) return _seen[memoKey];
  const i = ((_rot[mode] || 0) % pool.length + pool.length) % pool.length;
  _rot[mode] = i + 1;
  try { localStorage.setItem(ROT_KEY, JSON.stringify(_rot)); } catch (_) {}
  const id = pool[i].id;
  _seen[memoKey] = id;
  return id;
}

// pick a track id given the loaded manifest, the mode and the chapter's mood
export function pickTrack(tracks, mode, book, chapter) {
  if (!tracks?.length) return null;
  const key = `${book}_${chapter}`;
  if (mode === "ambient" || mode === "ancient" || mode === "hymns") {
    // Rotate sequentially through the whole pool for maximum variety.
    const pool = tracks.filter((t) => t.mode === mode);
    if (!pool.length) return null;
    return rotatePick(pool, mode, key);
  }
  // nature — rotate across all nature sounds for variety
  const pool = tracks.filter((t) => t.mode === "nature");
  return pool.length ? rotatePick(pool, "nature", key) : null;
}
